import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { userCompaniesService } from '../services/userCompaniesService';
import { watchlistService } from '../services/watchlistService';
import { useLocale } from '../contexts/LocaleContext';
import { useAppData } from '../contexts/AppDataContext';
import { COMPANY_TYPES } from '../models/watchlistModels';
import {
  Plus,
  Building2,
  Search,
  Trash2,
  Eye,
  Star,
  Upload,
} from 'lucide-react';
import CreateCompanyModal from '../components/Companies/CreateCompanyModal';
import BatchImportCompaniesModal from '../components/Companies/BatchImportCompaniesModal';
import { Card } from '../components/UI/card';
import { Button } from '../components/UI/button';
import { Input } from '../components/UI/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/UI/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../components/UI/pagination';
import { TableShimmer } from '../components/UI/table-shimmer';
import { colors } from '../config/colors';
import { spacing } from '../config/spacing';
import { getIndustryBadgeStyle, normalizeSupplierTier } from '../utils/companyDisplayUtils';
import toast from 'react-hot-toast';

const PAGE_SIZE = 10;

const SUPPLIER_CATEGORY_GROUPS = [
  { id: 'raw_material', labelKey: 'kanbanRawMaterial', types: [COMPANY_TYPES.RAW_MATERIAL_SUPPLIER] },
  {
    id: 'component_equipment',
    labelKey: 'kanbanComponentEquipment',
    types: [COMPANY_TYPES.COMPONENT_MANUFACTURER, COMPANY_TYPES.EQUIPMENT_SUPPLIER],
  },
  {
    id: 'finished_packaging',
    labelKey: 'kanbanFinishedPackaging',
    types: [COMPANY_TYPES.FINISHED_GOODS_MANUFACTURER, COMPANY_TYPES.PACKAGING_COMPANY],
  },
  {
    id: 'logistics_services',
    labelKey: 'kanbanLogisticsServices',
    types: [
      COMPANY_TYPES.LOGISTICS_COMPANY,
      COMPANY_TYPES.SERVICE_PROVIDER,
      COMPANY_TYPES.UTILITY_COMPANY,
      COMPANY_TYPES.CONTRACT_MANUFACTURER,
      COMPANY_TYPES.RESEARCH_DEVELOPMENT_COMPANY,
    ],
  },
  { id: 'unassigned', labelKey: 'kanbanUnassigned', types: ['unassigned'] },
];

function getSupplierCategoryGroupId(companyType) {
  const raw = (companyType ?? '').toString().trim().toLowerCase();
  const group = SUPPLIER_CATEGORY_GROUPS.find((g) =>
    g.types.some((t) => String(t).toLowerCase() === raw || (t === 'unassigned' && !raw))
  );
  return group ? group.id : 'unassigned';
}

const MyCompanies = () => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { watchlist, refreshWatchlist, userCompanies, refreshUserCompanies } = useAppData();
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [filterTier, setFilterTier] = useState('all');
  const [filterSupplierCategory, setFilterSupplierCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBatchImportModal, setShowBatchImportModal] = useState(false);
  const [watchlistActionId, setWatchlistActionId] = useState(null);

  const watchlistIds = useMemo(() => {
    const ids = new Set();
    (watchlist || []).forEach((item) => {
      const cid = item.companyId ?? item.company_id;
      if (cid) ids.add(cid);
    });
    return ids;
  }, [watchlist]);

  const industries = useMemo(() => {
    const list = companies || [];
    return [...new Set(list.map((c) => c.industry).filter(Boolean))].sort();
  }, [companies]);

  const cardStyle = useMemo(
    () => ({
      backgroundColor: colors.background.card ?? colors.background.secondary,
      borderColor: colors.border.light,
      boxShadow: spacing.shadow?.lg ?? '0 6px 18px rgba(32,41,50,0.04)',
      borderRadius: spacing.radius?.lg ?? 12,
    }),
    []
  );

  useEffect(() => {
    setCompanies(userCompanies || []);
  }, [userCompanies]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      await refreshUserCompanies();
      if (!cancelled) setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [refreshUserCompanies]);

  useEffect(() => {
    const tier = searchParams.get('tier');
    if (tier === '1' || tier === '2' || tier === '3') {
      setFilterTier(tier);
    }
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterIndustry, filterTier, filterSupplierCategory]);

  const supplierCategoryByTicker = useMemo(() => {
    const map = new Map();
    (watchlist || []).forEach((item) => {
      const ticker = item.companyId ?? item.company_id;
      if (!ticker) return;
      const type =
        item.userCompanyInfo?.companyType ??
        item.userCompanyInfo?.company_type ??
        item.user_company_info?.companyType ??
        item.user_company_info?.company_type ??
        item.companyType ??
        item.company_type ??
        '';
      map.set(String(ticker), getSupplierCategoryGroupId(type));
    });
    return map;
  }, [watchlist]);

  const handleCreateCompany = async (companyData) => {
    try {
      const result = await userCompaniesService.createUserCompany(undefined, companyData);
      if (result.success) {
        toast.success(t('companyCreatedSuccess'));
        setShowCreateModal(false);
        await refreshUserCompanies();
        const canonicalTicker = result.data?.ticker;
        if (canonicalTicker) {
          navigate(`/companies/${encodeURIComponent(canonicalTicker)}`);
        }
      } else {
        toast.error(result.message || t('failedToCreateCompany'));
      }
    } catch (error) {
      toast.error(t('failedToCreateCompany'));
    }
  };

  const handleDeleteCompany = async (ticker) => {
    if (!window.confirm(t('confirmDeleteCompany', { ticker }))) return;
    try {
      const result = await userCompaniesService.deleteUserCompany(undefined, ticker);
      if (result.success) {
        toast.success(t('companyDeletedSuccess'));
        await refreshUserCompanies();
      } else {
        toast.error(result.message || t('failedToDeleteCompany'));
      }
    } catch (error) {
      toast.error(t('failedToDeleteCompany'));
    }
  };

  const handleAddToWatchlist = async (ticker) => {
    try {
      setWatchlistActionId(ticker);
      const result = await watchlistService.addToCurrentUserWatchlist({
        company_id: ticker,
        priority: 'normal',
        notes: '',
        port_start_point: '',
        port_end_point: '',
      });
      if (result.success) {
        await refreshWatchlist();
        toast.success(t('addedToWatchlist'));
      } else {
        toast.error(result.message || t('failedToAddToWatchlist'));
      }
    } catch (error) {
      toast.error(t('failedToAddToWatchlist'));
    } finally {
      setWatchlistActionId(null);
    }
  };

  const filteredCompanies = useMemo(() => {
    let list = companies || [];
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      list = list.filter(
        (c) =>
          (c.name && c.name.toLowerCase().includes(q)) ||
          (c.ticker && c.ticker.toLowerCase().includes(q)) ||
          (c.industry && c.industry.toLowerCase().includes(q)) ||
          (c.country && c.country.toLowerCase().includes(q)) ||
          (c.description && c.description.toLowerCase().includes(q))
      );
    }
    if (filterIndustry !== 'all') {
      list = list.filter((c) => c.industry === filterIndustry);
    }
    if (filterTier !== 'all') {
      list = list.filter((c) => normalizeSupplierTier(c.tier) === String(filterTier));
    }
    if (filterSupplierCategory !== 'all') {
      list = list.filter((c) => {
        const groupId = supplierCategoryByTicker.get(String(c.ticker)) || 'unassigned';
        return groupId === filterSupplierCategory;
      });
    }
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [companies, searchTerm, filterIndustry, filterTier, filterSupplierCategory, supplierCategoryByTicker]);

  const paginatedCompanies = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredCompanies.slice(start, start + PAGE_SIZE);
  }, [filteredCompanies, page]);

  const totalPages = Math.max(1, Math.ceil(filteredCompanies.length / PAGE_SIZE));
  const from = filteredCompanies.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, filteredCompanies.length);

  const clearFilters = () => {
    setSearchTerm('');
    setFilterIndustry('all');
    setFilterTier('all');
    setFilterSupplierCategory('all');
    setPage(1);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Card className="p-6" style={{ backgroundColor: colors.background.secondary, borderColor: colors.border.light }}>
          <div className="flex flex-col gap-4">
            <div className="h-10 rounded animate-pulse" style={{ backgroundColor: colors.gray[200], width: '100%' }} />
            <div className="flex flex-wrap gap-3 pt-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-9 rounded animate-pulse" style={{ backgroundColor: colors.gray[200], width: 120 }} />
              ))}
            </div>
          </div>
        </Card>
        <TableShimmer rows={10} cardStyle={cardStyle} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search, filters and Add company — same layout as Companies */}
      <Card className="p-6" style={{ backgroundColor: colors.background.secondary, borderColor: colors.border.light }}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
                style={{ color: colors.text.tertiary }}
              />
              <Input
                type="text"
                placeholder={t('searchCompaniesPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full"
                style={{ borderColor: colors.border.light }}
              />
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button type="button" variant="outline" size="default" onClick={() => setShowBatchImportModal(true)}>
                <Upload className="h-4 w-4" />
                {t('batchImportCompanies')}
              </Button>
              <Button type="button" variant="default" size="default" onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4" />
                {t('createCompany')}
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 pt-2 border-t" style={{ borderColor: colors.border.light }}>
            <span className="text-sm font-medium" style={{ color: colors.text.secondary }}>
              {t('filtersLabel')}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium" style={{ color: colors.text.secondary }}>
                {t('supplierCategoryLabel')}
              </span>
              <select
                value={filterSupplierCategory}
                onChange={(e) => setFilterSupplierCategory(e.target.value)}
                className="rounded-lg border text-sm px-3 py-2 min-w-[180px] focus:outline-none focus:ring-2 focus:ring-primary-500"
                style={{ borderColor: colors.border.light, color: colors.text.primary, backgroundColor: colors.background.secondary }}
              >
                <option value="all">{t('allSupplierCategories')}</option>
                {SUPPLIER_CATEGORY_GROUPS.map((g) => (
                  <option key={g.id} value={g.id}>
                    {t(g.labelKey)}
                  </option>
                ))}
              </select>
            </div>
            <select
              value={filterTier}
              onChange={(e) => setFilterTier(e.target.value)}
              className="rounded-lg border text-sm px-3 py-2 min-w-[140px] focus:outline-none focus:ring-2 focus:ring-primary-500"
              style={{ borderColor: colors.border.light, color: colors.text.primary, backgroundColor: colors.background.secondary }}
            >
              <option value="all">{t('allSupplierTiers')}</option>
              <option value="1">{t('createSupplierTier1')}</option>
              <option value="2">{t('createSupplierTier2')}</option>
              <option value="3">{t('createSupplierTier3')}</option>
            </select>
            <select
              value={filterIndustry}
              onChange={(e) => setFilterIndustry(e.target.value)}
              className="rounded-lg border text-sm px-3 py-2 min-w-[140px] focus:outline-none focus:ring-2 focus:ring-primary-500"
              style={{ borderColor: colors.border.light, color: colors.text.primary, backgroundColor: colors.background.secondary }}
            >
              <option value="all">{t('allIndustries')}</option>
              {industries.map((ind) => (
                <option key={ind} value={ind}>
                  {(ind || '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={clearFilters}
              className="text-sm font-medium hover:underline"
              style={{ color: colors.primary[600] }}
            >
              {t('clearAll')}
            </button>
          </div>
        </div>
      </Card>

      {/* Table — same structure as Companies */}
      {filteredCompanies.length === 0 ? (
        <Card className="p-12 text-center" style={cardStyle}>
          <Building2 className="h-12 w-12 mx-auto mb-4" style={{ color: colors.gray[400] }} />
          <h3 className="text-lg font-medium mb-2" style={{ color: colors.text.primary }}>
            {searchTerm || filterIndustry !== 'all' ? t('noCompaniesFound') : t('noCompaniesYet')}
          </h3>
          <p className="mb-4 text-sm" style={{ color: colors.text.secondary }}>
            {searchTerm || filterIndustry !== 'all' ? t('tryAdjustingFilters') : t('createYourFirstCompany')}
          </p>
          {(searchTerm || filterIndustry !== 'all') && (
            <Button variant="outline" size="sm" onClick={clearFilters}>
              {t('clearFilters')}
            </Button>
          )}
          {!searchTerm && filterIndustry === 'all' && (
            <Button type="button" variant="default" size="sm" onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {t('createCompany')}
            </Button>
          )}
        </Card>
      ) : (
        <Card style={cardStyle} className="p-6">
          <div className="overflow-x-auto rounded-lg border" style={{ borderColor: colors.border.light }}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('tableCompany').toUpperCase()}</TableHead>
                  <TableHead>{t('createSupplierTierLabel').toUpperCase()}</TableHead>
                  <TableHead>{t('tableIndustry').toUpperCase()}</TableHead>
                  <TableHead>{t('tableLocation').toUpperCase()}</TableHead>
                  <TableHead>{t('tableActions').toUpperCase()}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedCompanies.map((company) => {
                  const industryStyle = getIndustryBadgeStyle(company.industry);
                  const initials = (company.name || '?').slice(0, 2).toUpperCase();
                  const inWatchlist = watchlistIds.has(company.ticker);

                  return (
                    <TableRow key={company.ticker}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold shrink-0"
                            style={{ backgroundColor: colors.primary[100], color: colors.primary[700] }}
                          >
                            {initials}
                          </div>
                          <div>
                            <button
                              type="button"
                              className="font-medium text-left hover:underline block"
                              style={{ color: colors.text.primary }}
                              onClick={() => navigate(`/companies/${company.ticker}`)}
                            >
                              {company.name}
                            </button>
                            <p className="text-xs" style={{ color: colors.text.secondary }}>
                              {company.ticker}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-medium" style={{ color: colors.text.primary }}>
                          {company.tier ? t(`createSupplierTier${company.tier}`) : '—'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span
                          className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium"
                          style={{ backgroundColor: industryStyle.bg, color: industryStyle.text }}
                        >
                          {company.industry || '—'}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm" style={{ color: colors.text.secondary }}>
                        {company.country || '—'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => navigate(`/companies/${company.ticker}`)}
                            className="flex items-center gap-1.5 font-medium text-link hover:underline"
                          >
                            <Eye className="h-4 w-4" />
                            {t('view')}
                          </button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => handleAddToWatchlist(company.ticker)}
                            disabled={watchlistActionId === company.ticker || inWatchlist}
                            aria-label={inWatchlist ? t('removeFromWatchlist') : t('addToWatchlist')}
                          >
                            <Star
                              className="h-4 w-4"
                              fill={inWatchlist ? 'currentColor' : 'none'}
                              style={{ color: inWatchlist ? colors.warning[500] : colors.text.secondary }}
                            />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => handleDeleteCompany(company.ticker)}
                            aria-label={t('remove')}
                            style={{ color: colors.danger[500] }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-4">
            <p className="text-sm" style={{ color: colors.text.secondary }}>
              {t('showingResults', { from, to, total: filteredCompanies.length })}
            </p>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} />
                </PaginationItem>
                {(() => {
                  const start = Math.max(1, page - 2);
                  const end = Math.min(totalPages, page + 2);
                  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
                })().map((p) => (
                    <PaginationItem key={p}>
                      <PaginationLink isActive={p === page} onClick={() => setPage(p)}>
                        {p}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                <PaginationItem>
                  <PaginationNext onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </Card>
      )}

      {showCreateModal && (
        <CreateCompanyModal onClose={() => setShowCreateModal(false)} onSubmit={handleCreateCompany} />
      )}
      {showBatchImportModal && (
        <BatchImportCompaniesModal
          onClose={() => setShowBatchImportModal(false)}
          onImported={refreshUserCompanies}
        />
      )}
    </div>
  );
};

export default MyCompanies;
